import { getShouldQualifyOfferHotelCancellation } from 'selectors/hotelCancellationSelectors'
import { hasAirportTransferPackageInclusions } from 'lib/offer/airportTransferPackageGrouping'
import AccommodationIndividualRoomTile from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTile'
import AccommodationIndividualRoomTileHeader from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileHeader'
import AccommodationIndividualRoomTileImage from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileImage'
import AccommodationIndividualRoomTileDetails from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileDetails'
import AccommodationIndividualRoomTilePricing from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTilePricing'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useCallback, useContext, useMemo } from 'react'
import WalledContent from 'components/Common/WalledContent'
import { isAvailableForPurchase, offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import TextButton from 'LuxKit/Button/TextButton'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import Group from 'components/utils/Group'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Clickable from 'components/Common/Clickable'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import FlashHotelIndividualRoomsPriceDetails from 'components/OfferPage/FlashHotel/FlashHotelIndividualRoomsPriceDetails'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import AccommodationAmenitiesAndPoliciesModal from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesModal'
import IndividualRoomPackageSelectionModal from './IndividualRoomPackageSelectionModal'
import MultipleRoomsInclusions from 'components/OfferPage/MultipleRoomsBooking/MultipleRoomsInclusions'
import OfferPageFocusAnchor from 'components/OfferPage/OfferPageFocusAnchor'
import { toOfferPageSnapshotId } from 'tripPlanner/assistant/surfaces/offerPage/offerPageSnapshotText'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import Sheet from 'components/utils/Sheet'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import GeoContext from 'contexts/geoContext'
import { unique } from 'lib/array/arrayUtils'
import { FormattedMessage, useIntl } from 'react-intl'

const LoyaltyPointsWrapper = styled.div`
  padding-block: ${rem(2)}; // To compensate for the loyalty coin which protrudes a couple of pixels outside of its container

  &:empty {
    display: none;
  }

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

interface Props {
  showHotelOnly: boolean
  packageNumber: number
  room: OfferPageRoom
  rooms: Array<OfferPageRoom>
  offer: App.HotelOffer
  packageView?: App.OfferPackageOptionView
  checkInDate: string | undefined
  checkOutDate: string | undefined
  flightsEnabled: boolean
  isFlightsSelected: boolean
  isFetching: boolean
  availableForPurchase: boolean
  availablePackages: Array<App.OfferPackageOptionView>
  /** Reward fare classes suppress the bundled-flight earn calculation (cash co-pay
   *  is not earnable). Hotel earn is unaffected. */
}

function MultiIndividualRoomRoom({
  room,
  rooms,
  offer,
  packageView,
  packageNumber,
  showHotelOnly,
  checkInDate,
  checkOutDate,
  flightsEnabled,
  isFlightsSelected,
  isFetching,
  availableForPurchase,
  availablePackages,
}: Props) {
  const intl = useIntl()
  const noRoomAvailableHeading = intl.formatMessage({
    id: 'BO8EbAIynm7k_a17y_Za',
    defaultMessage: 'No room available',
    description: 'Heading shown when no room is available in multi-room booking',
  })
  const logInToViewPricesLabel = intl.formatMessage({
    id: 'wv7nXZ9OI867682Vhavz',
    defaultMessage: 'Log in to view prices',
    description: 'Button text prompting user to log in to view room prices',
  })
  const cannotAccommodateHeading = intl.formatMessage({
    id: 'C1JFcjxxxoYbsVymXdGJ',
    defaultMessage: 'This room cannot accommodate your number of guests',
    description: 'Banner heading when selected room cannot fit the number of guests',
  })
  const soldOutHeading = intl.formatMessage({
    id: 'kf2poN5p1av63AbK01sj',
    defaultMessage: 'This room is sold out on your dates or max number of guests exceeded',
    description: 'Banner heading when room is sold out or max guests exceeded in multi-room booking',
  })
  const {
    totalFlightPrice,
    rewardAvailable,
    rewardSeatPointsPerPerson,
    rewardSeatCashCoPayPerPerson,
  } = useOfferPageFlightPrice()

  const roomPax = sumUpOccupancies([room])
  const totalPax = sumUpOccupancies(rooms)
  const flightPriceProportion = roomPax / totalPax

  const flightPrice = totalFlightPrice ? totalFlightPrice * flightPriceProportion : undefined

  // Per-room reward bundle split. Reward points/cash co-pay are per seat-eligible passenger;
  // we scale by this room's seat-eligible pax so the per-room totals add up to the bundle total.
  const roomSeatEligiblePax = (room.adults ?? 0) + (room.children ?? 0) + (room.teenagers ?? 0)
  const rewardPointsForRoom = rewardSeatPointsPerPerson != null
    ? rewardSeatPointsPerPerson * roomSeatEligiblePax
    : undefined
  const rewardCashCoPayForRoom = rewardSeatCashCoPayPerPerson != null
    ? rewardSeatCashCoPayPerPerson * roomSeatEligiblePax
    : undefined
  const isReward = !!rewardAvailable && rewardPointsForRoom !== undefined && rewardCashCoPayForRoom !== undefined

  const validCheckinCheckout = !!(checkInDate && checkOutDate)

  const hasFlights = flightsEnabled && isFlightsSelected

  const showModal = useContext(ModalContext)

  const offerPageDispatch = useOfferPageDispatch()

  const handleCheckAvailabilityClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const handleChangeDatesClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch])

  const showRoomDetails = useCallback(() => {
    if (packageView) {
      showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={packageView.package} />)
    }
  }, [packageView, showModal])

  const onClickChangeRoom = useCallback(() => {
    showModal(<IndividualRoomPackageSelectionModal
      roomId={room.id}
      availablePackages={availablePackages}
    />)
  }, [availablePackages, room.id, showModal])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView: packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView: packageView, flightPrice, skip: !hasFlights || isReward }),
  ], [hasFlights, offer, packageView, flightPrice, currentRegionCode, isReward])

  const onClickLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const canActivatePopularOptionBookingRemaining = !!packageView?.availableRooms && !!checkInDate
  const { isAnyVariant: isPopularOptionBookingRemainingVariant } = useExperiment(
    Experiments.croPopularOptionBookingRemaining,
    { canActivate: canActivatePopularOptionBookingRemaining },
  )
  const croPopularOptionBookingRemaining = canActivatePopularOptionBookingRemaining && isPopularOptionBookingRemainingVariant

  // After the rates are fetched, there's a small delay before the package autoselection runs (in an effect).
  // This causes the tile to briefly show a "No room available" message. Adding a new "room not selected yet" state
  // would be a pain, so let's just bridge the time gap between rates loading and rooms being selected by extending
  // the loading state by 200 milliseconds.
  const showAsFetchingWhilePackageMissing = useDelayedTogglingBoolean(isFetching, { offDelay: 200 }) && !packageView

  const roomTypesCount = unique(availablePackages.map((pkg) => pkg.package.roomType.id)).length

  if (!packageView) {
    if (showAsFetchingWhilePackageMissing) {
      return <Sheet padding="20 0" background="normal-background">
        <LoadingIndicator inline />
      </Sheet>
    } else if (availablePackages.every(view => !allRoomsOccupantsFitRoomCapacity(room, view.package.roomRate.capacities, offer))) {
      // No availability due to occupants exceeding capacity for all room types
      return <AccommodationIndividualRoomTile>
        <AccommodationIndividualRoomTileHeader room={room} heading={noRoomAvailableHeading} packageNumber={packageNumber} saleUnit={offer.saleUnit} />
        <AccommodationIndividualRoomTileDetails>
          <MessageBanner
            kind="warning"
            density="compact"
          >
            <FormattedMessage
              id="sJcHOXDrvDIuaWuk78Mh"
              defaultMessage="No room available, please <link>revise number of guests</link>"
              description="Warning when no room is available suggesting to revise number of guests"
              values={{
                link: (chunks) => <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>{chunks}</TextLink>,
              }}
            />
          </MessageBanner>
        </AccommodationIndividualRoomTileDetails>
      </AccommodationIndividualRoomTile>
    } else {
      // No availability due to dates
      return <AccommodationIndividualRoomTile>
        <AccommodationIndividualRoomTileHeader room={room} heading={noRoomAvailableHeading} packageNumber={packageNumber} saleUnit={offer.saleUnit} />
        <AccommodationIndividualRoomTileDetails>
          <MessageBanner
            kind="warning"
            density="compact"
          >
            <FormattedMessage
              id="IOCri3xMqkBvpX0FbsE7"
              defaultMessage="No room available, please <datesLink>try different dates</datesLink> or <guestsLink>revise number of guests</guestsLink>"
              description="Warning when no room is available suggesting to try different dates or revise number of guests"
              values={{
                datesLink: (chunks) => <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>{chunks}</TextLink>,
                guestsLink: (chunks) => <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>{chunks}</TextLink>,
              }}
            />
          </MessageBanner>
        </AccommodationIndividualRoomTileDetails>
      </AccommodationIndividualRoomTile>
    }
  }

  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = packageView.package?.ignoreDynamicCancellationPolicy ?? false
  const canSelectRoom = !!packageView.availableRooms && packageView.capacityValid
  const canChangeRoom = availablePackages.length > 1

  const roomOptionsAvailableText = canChangeRoom
    ? intl.formatMessage(
        {
          id: 'V8tTGHVdJnsKIhT-nQTN',
          defaultMessage: '{count, plural, one {# room option} other {# room options}} available',
          description: 'Text showing number of available room options in multi-room booking',
        },
        { count: availablePackages.length },
      )
    : intl.formatMessage({
        id: 'oEViJkIj1a46YTYnM3WO',
        defaultMessage: 'No additional options',
        description: 'Text shown when no additional room options are available',
      })

  const roomTypesAvailableText = canChangeRoom
    ? intl.formatMessage(
        {
          id: 'V8tTGHVdJnsKIhT-nQTN',
          defaultMessage: '{count, plural, one {# room option} other {# room options}} available',
          description: 'Text showing number of available room options in multi-room booking',
        },
        { count: roomTypesCount },
      )
    : intl.formatMessage({
        id: 'oEViJkIj1a46YTYnM3WO',
        defaultMessage: 'No additional options',
        description: 'Text shown when no additional room options are available',
      })

  const packageSnapshotId = toOfferPageSnapshotId(packageView.package.uniqueKey)

  return <AccommodationIndividualRoomTile roomPackage={packageView.package}>
    {packageSnapshotId && <OfferPageFocusAnchor targetId={`package:${packageSnapshotId}`} />}
    <AccommodationIndividualRoomTileHeader
      room={room}
      heading={packageView.package.roomType.name}
      packageNumber={packageNumber}
      saleUnit={offer.saleUnit}
      onTitleClick={showRoomDetails}
    />
    <AccommodationIndividualRoomTileImage roomPackage={packageView.package} offer={offer} />
    <AccommodationIndividualRoomTileDetails>
      <LabelGroup>
        {checkInDate && !croPopularOptionBookingRemaining && <RoomsRemainingLabel roomCount={packageView.availableRooms ?? 0} threshold="rate" />}
        {checkInDate && croPopularOptionBookingRemaining && !!packageView.availableRooms && <OfferUrgencyRoomCount availableRoomCount={packageView.availableRooms} /> }
        {!!offer.stats.booked && <BookingsCountLabel bookings={offer.stats.booked} threshold="room" />}
      </LabelGroup>
      <div>
        <Clickable onClick={showRoomDetails}>
          <AccommodationRoomDetailsList roomPackage={packageView.package} />
        </Clickable>
      </div>
      <div>
        {offer.type === OFFER_TYPE_ALWAYS_ON && <TAOCancellationPolicies
          offerId={offer.id}
          useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
          timezoneOffset={offer.property?.timezoneOffset}
          uniqueKey={packageView.package.uniqueKey}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          occupants={[room]}
          cancellationPolicy={packageView.package?.roomRate?.cancellationPolicy}
          offerType={offer.type}
          showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(packageView?.package)}
        />}
        {offer.type === OFFER_TYPE_HOTEL && availableForPurchase && <FlashCancellationPolicies
          checkInDate={checkInDate}
          // Multi-room is not used on charter routes; pass explicitly so a leftover cart
          // departure date is never applied on the offer page.
          charterDepartDate={undefined}
          propertyTimezoneOffset={offer.property?.timezoneOffset}
          cancellationPolicy={packageView.package.roomRate?.cancellationPolicy}
          numberOfDateChangesAllowed={offer.numberOfDateChangesAllowed}
          showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(packageView?.package)}
        />}
      </div>
      <LoyaltyPointsWrapper>
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequests}
          calculationType={(!checkInDate || isFlightsSelected) ? 'estimate' : 'exact'}
        />
      </LoyaltyPointsWrapper>
      <MultipleRoomsInclusions offer={offer} packageOption={packageView} room={room} />
    </AccommodationIndividualRoomTileDetails>
    <WalledContent
      enforced={offer.walledGarden}
      wall={<AccommodationIndividualRoomTilePricing desktopVerticalAlign="start">
        <TextButton
          kind="primary"
          size="medium"
          onClick={onClickLogin}
          fit="full-width"
        >
          {logInToViewPricesLabel}
        </TextButton>
        <Group direction="vertical" gap={8} verticalAlign="end" horizontalAlign="center">
          <TextButton fit="full-width" variant="default" kind="secondary" size="medium" startIcon={<LineEditAltIcon />} onClick={onClickChangeRoom} disabled={!canChangeRoom}>
            {intl.formatMessage({
              id: 'z25w3zmoOUVx1WEQiRCQ',
              defaultMessage: 'Change room',
              description: 'Button text to change room selection in multi-room booking',
            })}
          </TextButton>
          <Caption variant="medium" colour="light-foreground-alt">
            {roomOptionsAvailableText}
          </Caption>
        </Group>
      </AccommodationIndividualRoomTilePricing>}
    >
      <AccommodationIndividualRoomTilePricing>
        {!isFetching && validCheckinCheckout && !canSelectRoom && <>
          {!packageView.capacityValid && <MessageBanner
            kind="informational"
            icon={<LineExclamationCircleIcon />}
            heading={cannotAccommodateHeading}
          >
            <FormattedMessage
              id="acZenc88CRDcOrWDhPAi"
              defaultMessage="Choose another room or <link>add more rooms</link>"
              description="Suggestion to choose another room or add more rooms when room cannot accommodate guests"
              values={{
                link: (chunks) => <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>{chunks}</TextLink>,
              }}
            />
          </MessageBanner>}
          {!packageView.availableRooms && packageView.capacityValid && <MessageBanner
            kind="informational"
            icon={<LineExclamationCircleIcon />}
            heading={soldOutHeading}
          >
            <FormattedMessage
              id="JJoG1Er7O-Bb4lzZ5sQX"
              defaultMessage="Choose another room or <link>try different dates</link>"
              description="Suggestion to choose another room or try different dates when room is sold out"
              values={{
                link: (chunks) => <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>{chunks}</TextLink>,
              }}
            />
          </MessageBanner>}
        </>}
        <FlashHotelIndividualRoomsPriceDetails
          room={room}
          packageView={packageView}
          isFetching={isFetching}
          hasFlights={hasFlights}
          flightPrice={flightPrice}
          rewardAvailable={rewardAvailable}
          rewardPointsForRoom={rewardPointsForRoom}
          rewardCashCoPayForRoom={rewardCashCoPayForRoom}
          canShowDiscount={false}
        />
        <Group direction="vertical" gap={8} verticalAlign="end" horizontalAlign="center">
          <TextButton fit="full-width" variant="default" kind="secondary" size="medium" startIcon={<LineEditAltIcon />} onClick={onClickChangeRoom} disabled={!canChangeRoom}>
            {intl.formatMessage({
              id: 'z25w3zmoOUVx1WEQiRCQ',
              defaultMessage: 'Change room',
              description: 'Button text to change room selection in multi-room booking',
            })}
          </TextButton>
          <Caption variant="medium" colour="light-foreground-alt">
            {roomTypesAvailableText}
          </Caption>
        </Group>
      </AccommodationIndividualRoomTilePricing>
    </WalledContent>
  </AccommodationIndividualRoomTile>
}

type MappedProps = Pick<Props,
  | 'showHotelOnly'
  | 'offer'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'flightsEnabled'
  | 'isFlightsSelected'
  | 'availableForPurchase'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  showHotelOnly: getShouldQualifyOfferHotelCancellation(state),
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
  availableForPurchase: isAvailableForPurchase(state, appState),
}), MultiIndividualRoomRoom)
